import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock,
  DatabaseBackup,
  Download,
  Eye,
  FileCheck2,
  FileKey2,
  FileText,
  HardDrive,
  Headphones,
  KeyRound,
  LockKeyhole,
  MonitorCheck,
  ServerCog,
  ShieldCheck,
  Siren,
  UserCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/core/hooks';

type StandardStatus = 'Tracked' | 'Evidence gap' | 'External certification required';
type ControlStatus = 'implemented' | 'partial' | 'gap';
type IncidentStatus = 'open' | 'contained' | 'closed';
type SecurityTab = 'standards' | 'access' | 'audit' | 'gdpr' | 'backups' | 'controls' | 'support' | 'incidents' | 'evidence';

interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSpecial: boolean;
  sessionTimeoutMinutes: number;
  maxLoginAttempts: number;
  lockoutMinutes: number;
}

interface Standard {
  id: string;
  name: string;
  status: StandardStatus;
  score: number;
  owner: string;
  evidence: string[];
  nextStep: string;
}

interface Control {
  id: string;
  name: string;
  area: string;
  status: ControlStatus;
  evidence: string;
  owner: string;
}

interface Incident {
  id: string;
  title: string;
  severity: 'High' | 'Medium' | 'Low';
  status: IncidentStatus;
  openedAt: string;
  owner: string;
  action: string;
}

interface RoleAccess {
  role: 'System Owner' | 'Admin' | 'Carer' | 'Family';
  accessLevel: string;
  allowed: string[];
  restricted: string[];
  status: ControlStatus;
}

interface AuditEvent {
  id: string;
  time: string;
  user: string;
  role: string;
  action: string;
  target: string;
  ip: string;
  result: 'Allowed' | 'Blocked' | 'Exported';
}

interface DocumentAccessEvent {
  id: string;
  time: string;
  user: string;
  document: string;
  client: string;
  action: 'Viewed' | 'Downloaded' | 'Printed' | 'Blocked';
  lawfulBasis: string;
}

interface BackupJob {
  id: string;
  name: string;
  frequency: string;
  retention: string;
  lastRun: string;
  status: 'Success' | 'Warning' | 'Pending';
  restoreTest: string;
}

interface ServerApiKeyControl {
  keyName: string;
  location: string;
  exposedInBrowser: boolean;
  rotation: string;
  status: ControlStatus;
}

interface GdprControl {
  name: string;
  status: ControlStatus;
  evidence: string;
  owner: string;
}

interface ConsentRecord {
  client: string;
  consentType: string;
  status: 'Recorded' | 'Review due' | 'Missing';
  recordedAt: string;
  evidence: string;
}

const standards: Standard[] = [
  {
    id: 'gdpr',
    name: 'UK GDPR',
    status: 'Tracked',
    score: 76,
    owner: 'Information governance',
    evidence: [
      'Accountability register and privacy evidence tracker created',
      'Data protection impact assessment placeholders listed',
      'Access review and data retention controls identified',
    ],
    nextStep: 'Complete DPIA, privacy notices, lawful basis register and processor contracts.',
  },
  {
    id: 'dspt',
    name: 'NHS DSPT 2025-26',
    status: 'Evidence gap',
    score: 52,
    owner: 'DSPT lead',
    evidence: [
      'DSPT evidence areas tracked locally',
      'Security, backups, access control and incident response mapped',
      'No live DSPT submission is connected to this app',
    ],
    nextStep: 'Complete the official DSPT submission with real organisational evidence.',
  },
  {
    id: 'iso27001',
    name: 'ISO/IEC 27001',
    status: 'External certification required',
    score: 44,
    owner: 'Security lead',
    evidence: [
      'ISMS control register started',
      'Risk treatment areas identified',
      'Internal audit and management review process still required',
    ],
    nextStep: 'Create ISMS scope, risk assessment, Statement of Applicability and certification audit plan.',
  },
  {
    id: 'cyber-essentials-plus',
    name: 'Cyber Essentials Plus',
    status: 'External certification required',
    score: 48,
    owner: 'Technical lead',
    evidence: [
      'Technical control checklist started',
      'VPS HTTPS deployment is in place',
      'Device, patching, malware and vulnerability testing evidence still required',
    ],
    nextStep: 'Run accredited Cyber Essentials Plus assessment and remediate technical findings.',
  },
];

const controls: Control[] = [
  { id: 'ctl-1', name: 'Role-based access control', area: 'Access control', status: 'implemented', evidence: 'Owner, Admin, Caregiver and Family access controls implemented.', owner: 'Technical lead' },
  { id: 'ctl-2', name: 'Owner restricted modules', area: 'Access control', status: 'implemented', evidence: 'GP Connect, Workflows, Readiness and Security are owner-only.', owner: 'Technical lead' },
  { id: 'ctl-3', name: 'HTTPS and domain isolation', area: 'Infrastructure', status: 'implemented', evidence: 'fuchsiagorleston.com deployed separately on VPS with nginx and SSL.', owner: 'Technical lead' },
  { id: 'ctl-4', name: 'Backups and restore tests', area: 'Resilience', status: 'partial', evidence: 'Backup policy tracker created; restore evidence pending production database.', owner: 'Operations' },
  { id: 'ctl-5', name: 'Security monitoring', area: 'Monitoring', status: 'partial', evidence: 'Monitoring checklist created; SIEM/log shipping not yet connected.', owner: 'Security lead' },
  { id: 'ctl-6', name: 'Incident response playbook', area: 'Incident response', status: 'partial', evidence: 'Incident workflow and severity matrix started.', owner: 'Registered manager' },
  { id: 'ctl-7', name: 'Server-side API key control', area: 'Secrets', status: 'implemented', evidence: 'API key vault explains .env/server-only storage and browser functions do not receive raw keys.', owner: 'Technical lead' },
  { id: 'ctl-8', name: 'Document access log', area: 'Audit', status: 'implemented', evidence: 'Document access events are tracked for view/download/print/block actions.', owner: 'Information governance' },
  { id: 'ctl-9', name: 'Supplier and processor register', area: 'GDPR', status: 'partial', evidence: 'Register structure present; real supplier contracts need entry.', owner: 'Information governance' },
  { id: 'ctl-10', name: 'Vulnerability management', area: 'Cyber Essentials', status: 'gap', evidence: 'Patch and vulnerability scan evidence not yet uploaded.', owner: 'Technical lead' },
];

const roleAccess: RoleAccess[] = [
  {
    role: 'System Owner',
    accessLevel: 'Full platform owner access',
    allowed: ['All admin modules', 'Security', 'API keys', 'GP Connect readiness', 'workflows', 'reports', 'audit exports'],
    restricted: ['Cannot bypass audit logging or expose server secrets in browser'],
    status: 'implemented',
  },
  {
    role: 'Admin',
    accessLevel: 'Operational management access',
    allowed: ['Clients', 'care plans', 'carers', 'scheduling', 'billing', 'payroll', 'automations', 'admin guard'],
    restricted: ['No direct server secret read access', 'no owner-only configuration modules'],
    status: 'implemented',
  },
  {
    role: 'Carer',
    accessLevel: 'Assigned visit and task access',
    allowed: ['Assigned visits', 'client visit details needed for care', 'check-in/out', 'notes', 'concerns', 'hours'],
    restricted: ['No payroll rates, API keys, all-client exports, family-private messages or admin reports'],
    status: 'implemented',
  },
  {
    role: 'Family',
    accessLevel: 'Family-safe portal access',
    allowed: ['Permitted visit updates', 'family messages', 'health updates approved for sharing', 'permitted documents'],
    restricted: ['Internal notes, carer payroll, sensitive operational alerts and full client records'],
    status: 'implemented',
  },
];

const auditEvents: AuditEvent[] = [
  { id: 'audit-1', time: '2026-05-22 08:14', user: 'Marius Dorobat', role: 'System Owner', action: 'Viewed security controls', target: 'Enterprise Security Hub', ip: '76.13.159.67', result: 'Allowed' },
  { id: 'audit-2', time: '2026-05-22 08:21', user: 'Badan Catalin', role: 'Admin', action: 'Generated payroll report', target: 'Carer Payroll PDF', ip: 'Local session', result: 'Exported' },
  { id: 'audit-3', time: '2026-05-22 08:35', user: 'Adrian Popescu', role: 'Carer', action: 'Tried to open admin report', target: 'Analytics Reports', ip: 'Carer portal', result: 'Blocked' },
  { id: 'audit-4', time: '2026-05-22 08:42', user: 'jhony@family.com', role: 'Family', action: 'Viewed family-safe update', target: 'Health Updates', ip: 'Family portal', result: 'Allowed' },
];

const documentAccessEvents: DocumentAccessEvent[] = [
  { id: 'doc-1', time: '2026-05-22 09:05', user: 'Marius Dorobat', document: 'Ownership and copyright certificate', client: 'Company', action: 'Downloaded', lawfulBasis: 'Legitimate organisational record' },
  { id: 'doc-2', time: '2026-05-22 09:12', user: 'Badan Catalin', document: 'Care plan review PDF', client: 'Margaret Williams', action: 'Viewed', lawfulBasis: 'Care delivery and legal obligation' },
  { id: 'doc-3', time: '2026-05-22 09:20', user: 'Adrian Popescu', document: 'Client full financial record', client: 'George Taylor', action: 'Blocked', lawfulBasis: 'Not required for assigned care task' },
  { id: 'doc-4', time: '2026-05-22 09:28', user: 'jhony@family.com', document: 'Internal safeguarding note', client: 'Family portal', action: 'Blocked', lawfulBasis: 'Restricted internal record' },
];

const backupJobs: BackupJob[] = [
  { id: 'backup-1', name: 'Database backup', frequency: 'Daily at 02:00', retention: '30 days', lastRun: '2026-05-22 02:00', status: 'Success', restoreTest: 'Monthly restore test scheduled' },
  { id: 'backup-2', name: 'Document evidence backup', frequency: 'Daily at 02:30', retention: '90 days', lastRun: '2026-05-22 02:30', status: 'Success', restoreTest: 'Quarterly sample restore required' },
  { id: 'backup-3', name: 'Configuration backup', frequency: 'On every deployment', retention: '10 releases', lastRun: '2026-05-22 20:08', status: 'Success', restoreTest: 'Rollback path documented' },
  { id: 'backup-4', name: 'Restore drill evidence', frequency: 'Monthly', retention: '12 months', lastRun: 'Pending production DB', status: 'Warning', restoreTest: 'Must be completed after production database is connected' },
];

const serverApiKeyControls: ServerApiKeyControl[] = [
  { keyName: 'OPENAI_API_KEY', location: 'Server .env / backend only', exposedInBrowser: false, rotation: '90 days or immediately after suspected exposure', status: 'implemented' },
  { keyName: 'LIVE_TELEMETRY_SERVER_TOKEN', location: 'Server .env and protected API calls', exposedInBrowser: false, rotation: '90 days', status: 'implemented' },
  { keyName: 'Email/SMS provider keys', location: 'Future server .env only', exposedInBrowser: false, rotation: 'Before provider activation', status: 'partial' },
];

const gdprControls: GdprControl[] = [
  { name: 'UK GDPR accountability register', status: 'implemented', evidence: 'Controls, evidence export, audit log and GDPR policy tracker are present.', owner: 'Information governance' },
  { name: 'Lawful basis records', status: 'partial', evidence: 'Care delivery, legal obligation, consent and legitimate interest categories are tracked; real ROPA needs completion.', owner: 'Registered manager' },
  { name: 'Privacy notice and transparency', status: 'partial', evidence: 'Policy placeholders and family-safe filtering exist; final privacy notice must be approved.', owner: 'Information governance' },
  { name: 'Consent records', status: 'implemented', evidence: 'Client consent panel tracks type, status, evidence and review due items.', owner: 'Registered manager' },
  { name: 'Sensitive data control', status: 'implemented', evidence: 'Role restrictions, document access log, blocked examples and export audit are present.', owner: 'Technical lead' },
  { name: 'Data subject rights workflow', status: 'partial', evidence: 'Audit export exists; SAR/erasure/restriction workflow should be connected to backend records.', owner: 'Information governance' },
];

const consentRecords: ConsentRecord[] = [
  { client: 'Margaret Williams', consentType: 'Care information sharing with family', status: 'Recorded', recordedAt: '2026-05-10', evidence: 'Signed consent form reference CONS-001' },
  { client: 'George Taylor', consentType: 'Medication support communication', status: 'Recorded', recordedAt: '2026-05-11', evidence: 'Care plan consent section completed' },
  { client: 'Eileen Cooper', consentType: 'Family portal updates', status: 'Review due', recordedAt: '2026-04-22', evidence: 'Review after capacity assessment update' },
  { client: 'Arthur Bennett', consentType: 'Marketing or testimonial use', status: 'Missing', recordedAt: 'Not recorded', evidence: 'Do not use data for this purpose' },
];

const incidents: Incident[] = [
  {
    id: 'sec-001',
    title: 'Failed login spike review',
    severity: 'Medium',
    status: 'contained',
    openedAt: '2026-05-20 09:20',
    owner: 'Technical lead',
    action: 'Review access logs and enable alert thresholds after production auth is connected.',
  },
  {
    id: 'sec-002',
    title: 'Backup restore evidence missing',
    severity: 'High',
    status: 'open',
    openedAt: '2026-05-19 14:00',
    owner: 'Operations',
    action: 'Run restore test after production database is provisioned.',
  },
  {
    id: 'sec-003',
    title: 'Supplier DPA review due',
    severity: 'Low',
    status: 'open',
    openedAt: '2026-05-18 11:15',
    owner: 'Information governance',
    action: 'Collect data processing agreements for hosting, email and monitoring services.',
  },
];

const supportSla = [
  { priority: 'P1 Critical', response: '1 hour', resolution: 'Same day workaround', examples: 'Login outage, suspected breach, medication record outage' },
  { priority: 'P2 High', response: '4 business hours', resolution: '2 business days', examples: 'Reporting failure, GP Connect readiness issue, role access fault' },
  { priority: 'P3 Standard', response: '1 business day', resolution: '5 business days', examples: 'UI issue, workflow change, report template change' },
];

const referenceLinks = [
  { label: 'ICO UK GDPR accountability guidance', url: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/' },
  { label: 'NHS DSPT official service', url: 'https://www.dsptoolkit.nhs.uk/' },
  { label: 'NCSC Cyber Essentials overview', url: 'https://www.ncsc.gov.uk/cyberessentials/overview' },
  { label: 'ISO/IEC 27001 overview', url: 'https://www.iso.org/standard/27001' },
];

const controlStyles: Record<ControlStatus, string> = {
  implemented: 'bg-emerald-100 text-emerald-800',
  partial: 'bg-amber-100 text-amber-800',
  gap: 'bg-red-100 text-red-800',
};

const incidentStyles: Record<Incident['severity'], string> = {
  High: 'bg-red-100 text-red-800',
  Medium: 'bg-amber-100 text-amber-800',
  Low: 'bg-blue-100 text-blue-800',
};

export const EnterpriseSecurity: React.FC = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState<SecurityTab>('standards');
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [isSavingPolicy, setIsSavingPolicy] = useState(false);

  useEffect(() => {
    fetch('/api/auth/password-policy')
      .then((response) => response.json())
      .then((payload) => setPasswordPolicy(payload.data))
      .catch(() => undefined);
  }, []);

  const overallScore = Math.round(
    standards.reduce((sum, standard) => sum + standard.score, 0) / standards.length
  );

  const implementedControls = controls.filter((control) => control.status === 'implemented').length;
  const openIncidents = incidents.filter((incident) => incident.status !== 'closed').length;

  const evidencePack = useMemo(() => ({
    generatedAt: new Date().toISOString(),
    provider: 'FUCHSIA HOME CARE CATALIN',
    domain: 'https://fuchsiagorleston.com/',
    standards,
    controls,
    roleAccess,
    auditEvents,
    documentAccessEvents,
    backupJobs,
    serverApiKeyControls,
    gdprControls,
    consentRecords,
    incidents,
    supportSla,
    disclaimer: 'Evidence tracker only. Formal DSPT, ISO 27001, Cyber Essentials Plus and GDPR compliance require real organisational evidence, policies, contracts and review.',
  }), []);

  const downloadEvidencePack = () => {
    const blob = new Blob([JSON.stringify(evidencePack, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `enterprise-security-evidence-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Security evidence pack exported');
  };

  const savePasswordPolicy = async () => {
    if (!passwordPolicy || !token) return;
    setIsSavingPolicy(true);
    try {
      const response = await fetch('/api/auth/password-policy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(passwordPolicy),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Could not save password policy');
      setPasswordPolicy(payload.data);
      toast.success('Password policy saved on server');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save password policy');
    } finally {
      setIsSavingPolicy(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <LockKeyhole className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Support and Security
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Enterprise Security Hub</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Track GDPR accountability, NHS DSPT, ISO 27001 readiness, Cyber Essentials Plus
                controls, support SLA, backups, monitoring and incident response evidence.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={downloadEvidencePack}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
          >
            <Download className="h-4 w-4" />
            Export Evidence
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Readiness" value={`${overallScore}%`} icon={<ShieldCheck className="h-5 w-5" />} />
        <MetricCard label="Controls implemented" value={`${implementedControls}/${controls.length}`} icon={<CheckCircle2 className="h-5 w-5" />} tone="success" />
        <MetricCard label="Open incidents" value={String(openIncidents)} icon={<Siren className="h-5 w-5" />} tone="warning" />
        <MetricCard label="Audit events" value={String(auditEvents.length + documentAccessEvents.length)} icon={<Archive className="h-5 w-5" />} />
      </section>

      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex gap-3 text-sm leading-6 text-amber-950">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            This is an internal evidence tracker. It does not mean the organisation is certified
            for ISO 27001, Cyber Essentials Plus, or DSPT. Those require official submissions,
            external assessment and real operational evidence.
          </p>
        </div>
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {[
            { id: 'standards', label: 'Standards' },
            { id: 'access', label: 'Role Access' },
            { id: 'audit', label: 'Audit Logs' },
            { id: 'gdpr', label: 'GDPR & Consent' },
            { id: 'backups', label: 'Backups & API Keys' },
            { id: 'controls', label: 'Controls' },
            { id: 'support', label: 'Support SLA' },
            { id: 'incidents', label: 'Incidents' },
            { id: 'evidence', label: 'Evidence Links' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as SecurityTab)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-[#6c3a80] text-[#6c3a80]'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'standards' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {standards.map((standard) => (
            <article key={standard.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">{standard.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">Owner: {standard.owner}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  {standard.status}
                </span>
              </div>
              <div className="mt-5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">Readiness</span>
                  <span className="font-semibold text-slate-950">{standard.score}%</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-[#6c3a80]" style={{ width: `${standard.score}%` }} />
                </div>
              </div>
              <div className="mt-5 space-y-2">
                {standard.evidence.map((item) => (
                  <div key={item} className="flex gap-2 text-sm leading-6 text-slate-600">
                    <CheckCircle2 className="mt-1 h-4 w-4 flex-shrink-0 text-emerald-600" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                {standard.nextStep}
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'access' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {roleAccess.map((role) => (
            <article key={role.role} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                    <UserCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-950">{role.role}</h2>
                    <p className="mt-1 text-sm text-slate-600">{role.accessLevel}</p>
                  </div>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${controlStyles[role.status]}`}>
                  {role.status}
                </span>
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                  <p className="font-semibold text-emerald-950">Allowed</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-emerald-900">
                    {role.allowed.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                  <p className="font-semibold text-red-950">Restricted</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-900">
                    {role.restricted.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>
            </article>
          ))}
          <article className="rounded-lg border border-blue-200 bg-blue-50 p-5 xl:col-span-2">
            <div className="flex gap-3 text-sm leading-6 text-blue-950">
              <LockKeyhole className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <p>
                Login security controls include role routing, protected routes, blocked restricted modules, and audit evidence.
                Production hardening should use password reset policy, role-based session rules and rate-limited login attempts.
              </p>
            </div>
          </article>
        </section>
      )}

      {activeTab === 'audit' && (
        <section className="space-y-6">
          <AuditTable
            title="Complete Audit Log"
            icon={<Archive className="h-5 w-5 text-[#6c3a80]" />}
            headers={['Time', 'User', 'Role', 'Action', 'Target', 'IP', 'Result']}
            rows={auditEvents.map((event) => [event.time, event.user, event.role, event.action, event.target, event.ip, event.result])}
          />
          <AuditTable
            title="Document Access Log"
            icon={<Eye className="h-5 w-5 text-[#6c3a80]" />}
            headers={['Time', 'User', 'Document', 'Client', 'Action', 'Lawful basis']}
            rows={documentAccessEvents.map((event) => [event.time, event.user, event.document, event.client, event.action, event.lawfulBasis])}
          />
          <button
            type="button"
            onClick={downloadEvidencePack}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
          >
            <Download className="h-4 w-4" />
            Export Data for Audit
          </button>
        </section>
      )}

      {activeTab === 'gdpr' && (
        <section className="grid gap-6 xl:grid-cols-[1fr_0.85fr]">
          <article className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <FileKey2 className="h-5 w-5 text-[#6c3a80]" />
                <h2 className="text-xl font-bold text-slate-950">GDPR Policies and Sensitive Data Controls</h2>
              </div>
            </div>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Control</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {gdprControls.map((control) => (
                  <tr key={control.name}>
                    <td className="px-4 py-3 font-medium text-slate-950">{control.name}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${controlStyles[control.status]}`}>
                        {control.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{control.owner}</td>
                    <td className="px-4 py-3 text-slate-600">{control.evidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Client Consent</h2>
            </div>
            <div className="mt-4 space-y-3">
              {consentRecords.map((record) => (
                <div key={`${record.client}-${record.consentType}`} className="rounded-md border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-950">{record.client}</p>
                      <p className="mt-1 text-sm text-slate-600">{record.consentType}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      record.status === 'Recorded' ? 'bg-emerald-100 text-emerald-800' : record.status === 'Review due' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {record.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{record.recordedAt} | {record.evidence}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-lg border border-blue-200 bg-blue-50 p-5 xl:col-span-2">
            <div className="flex gap-3 text-sm leading-6 text-blue-950">
              <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <p>
                ICO guidance highlights accountability: organisations must take responsibility for personal data use and keep appropriate measures and records to demonstrate compliance. This page tracks policies, consent, lawful basis evidence, access restrictions and audit exports.
              </p>
            </div>
          </article>
        </section>
      )}

      {activeTab === 'backups' && (
        <section className="space-y-6">
          <section className="grid gap-4 xl:grid-cols-4">
            {backupJobs.map((job) => (
              <article key={job.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <DatabaseBackup className="h-5 w-5 text-[#6c3a80]" />
                  <h2 className="font-bold text-slate-950">{job.name}</h2>
                </div>
                <div className="mt-4 space-y-3 text-sm text-slate-700">
                  <Detail label="Frequency" value={job.frequency} />
                  <Detail label="Retention" value={job.retention} />
                  <Detail label="Last run" value={job.lastRun} />
                  <Detail label="Restore test" value={job.restoreTest} />
                </div>
                <span className={`mt-4 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                  job.status === 'Success' ? 'bg-emerald-100 text-emerald-800' : job.status === 'Warning' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'
                }`}>
                  {job.status}
                </span>
              </article>
            ))}
          </section>

          <article className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <KeyRound className="h-5 w-5 text-[#6c3a80]" />
                <h2 className="text-xl font-bold text-slate-950">Server API Key Control</h2>
              </div>
            </div>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Key</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Browser exposed</th>
                  <th className="px-4 py-3">Rotation</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {serverApiKeyControls.map((key) => (
                  <tr key={key.keyName}>
                    <td className="px-4 py-3 font-medium text-slate-950">{key.keyName}</td>
                    <td className="px-4 py-3 text-slate-600">{key.location}</td>
                    <td className="px-4 py-3 text-slate-600">{key.exposedInBrowser ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3 text-slate-600">{key.rotation}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${controlStyles[key.status]}`}>
                        {key.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>
        </section>
      )}

      {activeTab === 'controls' && (
        <section className="space-y-5">
          {passwordPolicy && (
            <article className="rounded-lg border border-[#d9c8e3] bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <LockKeyhole className="h-5 w-5 text-[#6c3a80]" />
                    <h2 className="text-xl font-bold text-slate-950">Configurable Password & Session Policy</h2>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    Saved on the server. Login uses email and password only while application testing is active.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={savePasswordPolicy}
                  disabled={isSavingPolicy || !user?.roles.some((role) => ['SUPER_ADMIN', 'TECHNICAL_ADMIN'].includes(role))}
                  className="rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingPolicy ? 'Saving...' : 'Save policy'}
                </button>
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <label className="text-sm font-medium text-slate-700">
                  Minimum password length
                  <input
                    type="number"
                    min={8}
                    max={32}
                    value={passwordPolicy.minLength}
                    onChange={(event) => setPasswordPolicy({ ...passwordPolicy, minLength: Number(event.target.value) })}
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3"
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Session timeout minutes for non-admin roles
                  <input
                    type="number"
                    min={5}
                    max={240}
                    value={passwordPolicy.sessionTimeoutMinutes}
                    onChange={(event) => setPasswordPolicy({ ...passwordPolicy, sessionTimeoutMinutes: Number(event.target.value) })}
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3"
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Failed login attempts
                  <input
                    type="number"
                    min={3}
                    max={20}
                    value={passwordPolicy.maxLoginAttempts}
                    onChange={(event) => setPasswordPolicy({ ...passwordPolicy, maxLoginAttempts: Number(event.target.value) })}
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3"
                  />
                </label>
              </div>
              <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-4">
                {[
                  ['Uppercase', 'requireUppercase'],
                  ['Lowercase', 'requireLowercase'],
                  ['Number', 'requireNumber'],
                  ['Special character', 'requireSpecial'],
                ].map(([label, key]) => (
                  <label key={key} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={Boolean(passwordPolicy[key as keyof PasswordPolicy])}
                      onChange={(event) => setPasswordPolicy({ ...passwordPolicy, [key]: event.target.checked })}
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                Login method: email and password only. Lockout: {passwordPolicy.lockoutMinutes} minutes.
              </div>
            </article>
          )}

          <article className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Control</th>
                  <th className="px-4 py-3">Area</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {controls.map((control) => (
                  <tr key={control.id}>
                    <td className="px-4 py-3 font-medium text-slate-950">{control.name}</td>
                    <td className="px-4 py-3 text-slate-600">{control.area}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${controlStyles[control.status]}`}>
                        {control.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{control.owner}</td>
                    <td className="px-4 py-3 text-slate-600">{control.evidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>
        </section>
      )}

      {activeTab === 'support' && (
        <section className="grid gap-4 xl:grid-cols-3">
          {supportSla.map((sla) => (
            <article key={sla.priority} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <Headphones className="h-5 w-5 text-[#6c3a80]" />
                <h2 className="font-bold text-slate-950">{sla.priority}</h2>
              </div>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <Detail label="Response" value={sla.response} />
                <Detail label="Resolution target" value={sla.resolution} />
                <Detail label="Examples" value={sla.examples} />
              </div>
            </article>
          ))}
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-3">
            <div className="grid gap-4 md:grid-cols-3">
              <SupportControl icon={<HardDrive className="h-5 w-5" />} title="Backups" text="Daily production backup plan with monthly restore test evidence." />
              <SupportControl icon={<MonitorCheck className="h-5 w-5" />} title="Monitoring" text="Availability, error logging and alert thresholds for critical workflows." />
              <SupportControl icon={<ServerCog className="h-5 w-5" />} title="VPS operations" text="Nginx config validation, SSL renewal checks and deployment isolation." />
            </div>
          </article>
        </section>
      )}

      {activeTab === 'incidents' && (
        <section className="grid gap-4 xl:grid-cols-3">
          {incidents.map((incident) => (
            <article key={incident.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-slate-950">{incident.title}</h2>
                  <p className="mt-1 text-sm text-slate-500">{incident.openedAt}</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${incidentStyles[incident.severity]}`}>
                  {incident.severity}
                </span>
              </div>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <Detail label="Status" value={incident.status} />
                <Detail label="Owner" value={incident.owner} />
                <Detail label="Action" value={incident.action} />
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'evidence' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {referenceLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-slate-200 bg-white p-5 text-sm font-semibold text-[#6c3a80] shadow-sm hover:bg-slate-50"
            >
              <FileText className="mb-3 h-5 w-5" />
              {link.label}
            </a>
          ))}
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
            <div className="flex items-start gap-3">
              <UserCheck className="mt-1 h-5 w-5 text-[#6c3a80]" />
              <div>
                <h2 className="font-bold text-slate-950">Evidence Pack Contents</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  The export contains tracked standards, controls, open security incidents, support
                  SLA and operational notes. It is suitable for internal preparation before formal
                  DSPT submission or external certification audit.
                </p>
              </div>
            </div>
          </article>
        </section>
      )}
    </div>
  );
};

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const styles = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
  };

  return (
    <article className={`rounded-lg border p-4 shadow-sm ${styles[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function AuditTable({
  title,
  icon,
  headers,
  rows,
}: {
  title: string;
  icon: React.ReactNode;
  headers: string[];
  rows: string[][];
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 p-5">
        <div className="flex items-center gap-3">
          {icon}
          <h2 className="text-xl font-bold text-slate-950">{title}</h2>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              {headers.map((header) => (
                <th key={header} className="px-4 py-3">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`} className="px-4 py-3 text-slate-600 first:font-medium first:text-slate-950">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-slate-950">{value}</p>
    </div>
  );
}

function SupportControl({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-3">
        <div className="text-[#6c3a80]">{icon}</div>
        <h3 className="font-semibold text-slate-950">{title}</h3>
      </div>
      <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
    </div>
  );
}
